import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import axios from 'axios';
import { CreditCard, Check, Sparkles, GraduationCap, Wallet } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BuyPoints = () => {
  const { refreshUser } = useAuth();
  const [packages, setPackages] = useState([]);
  const [config, setConfig] = useState({ points_per_rupee: 2, min_inr: 10, max_inr: 200 });
  const [selectedInr, setSelectedInr] = useState(100);
  const [customInr, setCustomInr] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchPackages = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/points/packages`);
      setPackages(data.packages);
      setConfig(data.rate);
    } catch (e) {
      // fallback to static defaults
      setPackages([
        { inr: 50, points: 100, popular: false, label: 'Starter' },
        { inr: 100, points: 200, popular: true, label: 'Most Loved' },
        { inr: 150, points: 300, popular: false, label: 'Power Up' },
        { inr: 200, points: 400, popular: false, label: 'Max Pack' },
      ]);
    }
  }, []);

  useEffect(() => { fetchPackages(); }, [fetchPackages]);

  const finalInr = customInr ? Math.min(Math.max(parseInt(customInr, 10) || 0, config.min_inr), config.max_inr) : selectedInr;
  const pointsCredited = finalInr * config.points_per_rupee;
  const GST_RATE = 28;
  const gstAmount = +(finalInr * GST_RATE / (100 + GST_RATE)).toFixed(2);
  const baseAmount = +(finalInr - gstAmount).toFixed(2);

  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const s = document.createElement('script');
      s.src = 'https://checkout.razorpay.com/v1/checkout.js';
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });

  const handleBuyPoints = async () => {
    if (finalInr < config.min_inr) {
      toast.error(`Minimum purchase is ₹${config.min_inr}`);
      return;
    }
    if (finalInr > config.max_inr) {
      toast.error(`Maximum purchase is ₹${config.max_inr} per transaction`);
      return;
    }
    setLoading(true);
    const ok = await loadRazorpayScript();
    if (!ok) { toast.error('Failed to load payment gateway'); setLoading(false); return; }

    try {
      const { data } = await axios.post(`${API_URL}/api/points/buy`, { amount: finalInr });
      const options = {
        key: data.key_id,
        amount: data.amount,            // already in paise
        currency: 'INR',
        name: 'Aptivon',
        description: `${data.breakdown.points_credited} Points`,
        order_id: data.order_id,
        handler: async (response) => {
          try {
            await axios.post(`${API_URL}/api/points/verify-payment`, {
              order_id: response.razorpay_order_id,
              payment_id: response.razorpay_payment_id,
              signature: response.razorpay_signature,
            });
            toast.success(`Added ${data.breakdown.points_credited} points to your wallet!`);
            await refreshUser();
          } catch (err) {
            toast.error('Payment verification failed');
          }
          setLoading(false);
        },
        theme: { color: '#0EA5E9' },
        modal: { ondismiss: () => setLoading(false) },
      };
      new window.Razorpay(options).open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate payment');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="buy-points-page">
      <Header />
      <main className="max-w-5xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-emerald-100 to-teal-100 border border-emerald-200 mb-4">
            <GraduationCap className="w-4 h-4 text-emerald-700" />
            <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider">Student-Friendly Pricing</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-[#111827] mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Top Up Your Points
          </h1>
          <p className="text-base text-[#6B7280]">
            Pay in rupees, get <span className="font-black text-emerald-600">2× points</span> back. Capped at ₹{config.max_inr} per transaction so you never overspend.
          </p>
        </div>

        {/* Rate banner */}
        <div className="max-w-md mx-auto mb-10 p-5 rounded-3xl bg-gradient-to-br from-cyan-500 to-blue-600 shadow-[0_6px_0_rgb(2,132,199)] text-white" data-testid="rate-banner">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider opacity-90">Exchange rate</p>
              <p className="text-3xl font-black mt-1 flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                ₹1 = {config.points_per_rupee} <Sparkles className="w-6 h-6" /> pts
              </p>
            </div>
            <div className="text-right opacity-95">
              <p className="text-xs">Min ₹{config.min_inr}</p>
              <p className="text-xs">Max ₹{config.max_inr}</p>
            </div>
          </div>
        </div>

        {/* Packages */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {packages.map((pkg) => {
            const isSelected = !customInr && selectedInr === pkg.inr;
            return (
              <button
                key={pkg.inr}
                onClick={() => { setSelectedInr(pkg.inr); setCustomInr(''); }}
                data-testid={`package-${pkg.inr}`}
                className={`quiz-card p-5 text-center transition-all relative ${
                  isSelected ? 'ring-4 ring-[#0EA5E9] shadow-xl -translate-y-1' : 'hover:shadow-lg hover:-translate-y-0.5'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full text-[10px] font-black uppercase tracking-wider">
                    {pkg.label}
                  </div>
                )}
                {isSelected && (
                  <div className="absolute top-2.5 right-2.5 w-6 h-6 bg-[#0EA5E9] rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
                <p className="text-xs text-[#6B7280] uppercase tracking-wider mb-1">{pkg.label}</p>
                <p className="text-3xl font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  {pkg.points}
                </p>
                <p className="text-xs text-[#9CA3AF]">points</p>
                <div className="pt-3 mt-3 border-t border-slate-100">
                  <p className="text-lg font-black text-[#0EA5E9]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    ₹{pkg.inr}
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Custom amount */}
        <div className="max-w-md mx-auto mb-8" data-testid="custom-amount-section">
          <label className="block text-xs font-bold uppercase tracking-widest text-[#6B7280] mb-2">
            Or enter a custom amount (₹{config.min_inr}–₹{config.max_inr})
          </label>
          <div className="flex items-center gap-2">
            <span className="px-4 py-3 bg-slate-100 rounded-l-2xl text-slate-600 font-bold">₹</span>
            <input
              type="number"
              min={config.min_inr}
              max={config.max_inr}
              step={1}
              value={customInr}
              onChange={(e) => setCustomInr(e.target.value)}
              data-testid="custom-amount-input"
              placeholder={`e.g. 75`}
              className="flex-1 px-4 py-3 rounded-r-2xl border-2 border-slate-200 focus:border-[#0EA5E9] focus:outline-none font-bold text-[#111827]"
            />
            {customInr && (
              <button
                onClick={() => setCustomInr('')}
                className="text-xs text-slate-500 hover:text-slate-700 font-medium px-2"
                data-testid="clear-custom-btn"
              >
                Clear
              </button>
            )}
          </div>
          {customInr && parseInt(customInr, 10) > config.max_inr && (
            <p className="text-xs text-amber-600 mt-1.5 font-medium">Capped at ₹{config.max_inr} for student safety</p>
          )}
        </div>

        {/* Order Summary */}
        <div className="quiz-card p-7 max-w-md mx-auto" data-testid="order-summary">
          <div className="text-center mb-5">
            <Wallet className="w-10 h-10 text-[#0EA5E9] mx-auto mb-3" />
            <h3 className="text-xl font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Order Summary</h3>
            <p className="text-5xl font-black text-[#0EA5E9] mt-2" style={{ fontFamily: 'Fredoka, sans-serif' }} data-testid="points-credited">
              {pointsCredited.toLocaleString()}
            </p>
            <p className="text-sm text-[#6B7280] mt-1">points credited instantly</p>
          </div>

          <div className="bg-slate-50 rounded-xl p-4 mb-5 space-y-2" data-testid="gst-breakdown">
            <div className="flex justify-between text-sm">
              <span className="text-[#6B7280]">Base Amount</span>
              <span className="font-medium text-[#111827]">₹{baseAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#6B7280]">GST ({GST_RATE}%)</span>
              <span className="font-medium text-[#111827]">₹{gstAmount.toFixed(2)}</span>
            </div>
            <div className="border-t border-slate-200 pt-2 flex justify-between">
              <span className="font-bold text-[#111827]">You pay</span>
              <span className="font-black text-[#0EA5E9] text-lg" data-testid="final-inr">₹{finalInr}</span>
            </div>
            <p className="text-[10px] text-[#9CA3AF] mt-1">GST inclusive · Tax invoice issued on payment</p>
          </div>

          <button
            onClick={handleBuyPoints}
            disabled={loading || finalInr < config.min_inr}
            className="w-full px-6 py-4 bg-[#0EA5E9] text-white rounded-2xl font-bold shadow-[0_4px_0_rgb(2,132,199)] active:translate-y-1 active:shadow-none transition-all disabled:opacity-50 disabled:cursor-not-allowed text-base flex items-center justify-center gap-2"
            data-testid="proceed-payment-button"
          >
            <CreditCard className="w-5 h-5" />
            {loading ? 'Processing…' : `Pay ₹${finalInr} · Get ${pointsCredited} pts`}
          </button>
          <p className="text-xs text-center text-[#6B7280] mt-3">Secure payment via Razorpay · UPI · Cards · NetBanking</p>
        </div>
      </main>
    </div>
  );
};

export default BuyPoints;
